import { useT } from "@transifex/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AeaExerciseModel, BandUnit, GetBandInfoResponse, SchoolClass, UnitCard } from "p6m-p6u";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { useGeneralContext, type MakePxpRequest } from "../../context/AppGeneralContext";
import { getRequest, sortAndFilterUnitsForBandResponse } from "../../helpers/networkHelper";
import { sanitizeText } from "../../helpers/TestCreation";
import { fetchUserUnitsWithCards } from "../../helpers/UserExercises";
import { getCardsByIds } from "../../networking/tests";
import { useSchoolClassOperations } from "../../hooks/useSchoolClassOperations";
import LoadingSpinner from "../../basic/loadingSpinner/LoadingSpinner";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { AreaButton, ButtonColorVariant, ButtonVariant, IconPosition } from "../teacherArea/AreaButton";
import { ClassActivityCardExpandedContent } from "./ClassActivityCardExpandedContent";
import { toggleInSet } from "../../helpers/SetHelper";

const MAX_UNIT_NAME_WIDTH = 200;

interface VocabWordGroup {
    setUuid: string;
    unitName: string;
    words: string[];
    orderId: number;
}

const _extractWords = (cards: UnitCard[]): string[] => {
    return cards.map((c) => (c.question_text ? sanitizeText(c.question_text) : "")).filter(Boolean);
};

const _groupCardsByUnit = (cards: UnitCard[], units: BandUnit[]): VocabWordGroup[] => {
    const groupedBySet: Record<string, UnitCard[]> = {};
    for (const card of cards) {
        const setUuid = card.item_set_uuid || "";
        if (!groupedBySet[setUuid]) groupedBySet[setUuid] = [];
        groupedBySet[setUuid].push(card);
    }

    return Object.entries(groupedBySet)
        .map(([setUuid, groupCards]) => {
            const matchingUnit = units.find((u) => u.uuid === setUuid);
            return {
                setUuid,
                unitName: matchingUnit?.name || "",
                words: _extractWords(groupCards),
                orderId: matchingUnit?.order_id || 0,
            };
        })
        .sort((a, b) => a.orderId - b.orderId);
};

const _fetchOwnContentGroups = async (
    makePxpRequest: MakePxpRequest,
    subjectUuid: string,
    includedCardIds: Set<string>
): Promise<VocabWordGroup[]> => {
    const unitsWithCards = await fetchUserUnitsWithCards(makePxpRequest, subjectUuid);

    return unitsWithCards
        .map(({ unit, cards }) => ({
            setUuid: unit.id,
            unitName: unit.name,
            words: cards.filter((c) => c.id && includedCardIds.has(c.id)).map((c) => c.question || ""),
            orderId: unit.order,
        }))
        .filter((g) => g.words.length > 0)
        .sort((a, b) => a.orderId - b.orderId);
};

export interface VocabTrainingCardExpandedContentProps {
    vocabTraining: AeaExerciseModel;
    availableSchoolClasses: SchoolClass[];

    bookInfo?: GetBandInfoResponse;
    ownSubjectInfo?: { name?: string; primaryLang: string; secondaryLang?: string };
    vocabCount?: number;
    dateLabel?: string;
    selectedSchoolClassId?: string;
    onShare?: () => void;
    visibilityBadge?: ReactNode;
}

export const VocabTrainingCardExpandedContent = (props: VocabTrainingCardExpandedContentProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint, isAboveMD } = useBreakpoints();
    const { makePxpRequest } = useGeneralContext();
    const { handleVocabTrainingSchoolClassChange } = useSchoolClassOperations();
    const makePxpRequestRef = useRef(makePxpRequest);
    makePxpRequestRef.current = makePxpRequest;

    const t_share = t("Share", { _tags: "AssistantOverview" });
    const t_assignedVocab = t("Assigned Vocabulary", { _tags: "ClassActivity" });
    const t_loadVocabError = t("Vocabulary could not be loaded.", { _tags: "ClassActivity" });
    const t_showMore = t("Show more", { _tags: "ClassActivity" });
    const t_showLess = t("Show less", { _tags: "ClassActivity" });

    const [vocabWordGroups, setVocabWordGroups] = useState<VocabWordGroup[]>([]);
    const [isLoadingCards, setIsLoadingCards] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [overflowingGroups, setOverflowingGroups] = useState<Set<string>>(new Set());
    const [widestUnitNameWidth, setWidestUnitNameWidth] = useState<number | undefined>();

    const unitNameRefs = useRef<(HTMLParagraphElement | null)[]>([]);
    const wordRefs = useRef<Record<string, HTMLParagraphElement | null>>({});

    const isOwnContent = props.vocabTraining.isOwnContentTask || !!props.ownSubjectInfo;
    const cardIds = props.vocabTraining.cardIds;
    const bandId = props.bookInfo?.band?.ID;
    const ownSubjectId = props.vocabTraining.subjectContentId?.id;

    const cardIdsKey = useMemo(
        () =>
            (cardIds ?? [])
                .map((c) => c.id)
                .filter(Boolean)
                .join(","),
        [cardIds]
    );

    useEffect(() => {
        if (!cardIdsKey) return;

        let cancelled = false;

        const loadVocabCards = async () => {
            setIsLoadingCards(true);
            setHasError(false);
            setExpandedGroups(new Set());

            try {
                const ids = cardIdsKey.split(",");

                if (isOwnContent && ownSubjectId) {
                    const groups = await _fetchOwnContentGroups(makePxpRequestRef.current, ownSubjectId, new Set(ids));
                    if (cancelled) return;
                    setVocabWordGroups(groups);
                    return;
                }

                const [cardsResponse, unitsResponse] = await Promise.all([
                    getCardsByIds(ids),
                    bandId ? getRequest("p6u/get-units-for-band/", { bandId }) : Promise.resolve(null),
                ]);

                if (cancelled) return;

                const cards = cardsResponse.items || [];

                if (bandId && unitsResponse) {
                    const units: BandUnit[] = sortAndFilterUnitsForBandResponse(unitsResponse.data);
                    setVocabWordGroups(_groupCardsByUnit(cards, units));
                } else {
                    setVocabWordGroups([
                        { setUuid: "own-content", unitName: "", words: _extractWords(cards), orderId: 0 },
                    ]);
                }
            } catch (e) {
                if (cancelled) return;
                console.log("Error loading vocab cards", e);
                setHasError(true);
            } finally {
                if (!cancelled) setIsLoadingCards(false);
            }
        };

        loadVocabCards();

        return () => {
            cancelled = true;
        };
    }, [cardIdsKey, isOwnContent, bandId, ownSubjectId]);

    useLayoutEffect(() => {
        if (vocabWordGroups.every((g) => !g.unitName)) return;
        const elements = unitNameRefs.current.filter(Boolean) as HTMLParagraphElement[];
        if (elements.length === 0) return;
        const max = Math.max(...elements.map((el) => el.getBoundingClientRect().width));
        setWidestUnitNameWidth(max);
    }, [vocabWordGroups, isAboveMD]);

    // Detect which rows are clamped (overflow their 2-line preview) so only those get a toggle.
    // Expanded rows can't be measured (clamp removed), so assume they were truncatable.
    useLayoutEffect(() => {
        const next = new Set<string>();
        vocabWordGroups.forEach((group) => {
            if (expandedGroups.has(group.setUuid)) {
                next.add(group.setUuid);
                return;
            }
            const el = wordRefs.current[group.setUuid];
            if (el && el.scrollHeight > el.clientHeight + 1) next.add(group.setUuid);
        });
        setOverflowingGroups(next);
    }, [vocabWordGroups, isAboveMD, expandedGroups]);

    const handleSchoolClassChange = async (classId: string | null) => {
        const trainingId = props.vocabTraining.id;
        if (!trainingId) return false;
        return handleVocabTrainingSchoolClassChange(trainingId, classId);
    };

    const toggleGroup = (setUuid: string) => {
        setExpandedGroups((prev) => toggleInSet(prev, setUuid));
    };

    const renderVocabWords = () => {
        if (isLoadingCards) {
            return (
                <div className="flex justify-center p-l">
                    <LoadingSpinner
                        size="h-l w-l"
                        colorClass="border-p6-grey-light border-t-p6-grey-dark"
                    />
                </div>
            );
        }

        if (hasError) {
            return (
                <div className="border border-p6-grey-light rounded-[20px] bg-white p-m">
                    <p className="text-l text-p6-grey-dark">{t_loadVocabError}</p>
                </div>
            );
        }

        if (vocabWordGroups.length === 0) return null;

        return (
            <div className="border border-p6-grey-light rounded-[20px] overflow-clip">
                <div className="bg-white p-m border-b border-forest-green-800">
                    <p className="font-semibold text-l text-p6-grey-dark">{t_assignedVocab}</p>
                </div>
                {vocabWordGroups.map((group, index) => {
                    const isLastRow = index === vocabWordGroups.length - 1;
                    const isExpanded = expandedGroups.has(group.setUuid);
                    const isOverflowing = overflowingGroups.has(group.setUuid);

                    return (
                        <div
                            key={group.setUuid}
                            className={`bg-white p-m text-l text-p6-grey-dark ${
                                isLastRow ? "" : "border-b border-p6-grey-light"
                            } flex ${getStyleBasedOnBreakpoint(Breakpoint.MD, "flex-row gap-m", "flex-col gap-s")}`}
                        >
                            {group.unitName && (
                                <p
                                    ref={(el) => {
                                        unitNameRefs.current[index] = el;
                                    }}
                                    className="font-semibold shrink-0 break-words"
                                    style={{
                                        maxWidth: isAboveMD ? MAX_UNIT_NAME_WIDTH : undefined,
                                        minWidth: isAboveMD ? widestUnitNameWidth : undefined,
                                    }}
                                >
                                    {group.unitName}
                                </p>
                            )}
                            <div className="flex-1 min-w-0">
                                <p
                                    ref={(el) => {
                                        wordRefs.current[group.setUuid] = el;
                                    }}
                                    className={`break-words ${isExpanded ? "" : "line-clamp-2"}`}
                                >
                                    {group.words.join(" • ")}
                                </p>
                                {isOverflowing && (
                                    <button
                                        type="button"
                                        onClick={() => toggleGroup(group.setUuid)}
                                        className="flex items-center gap-xs min-h-xl text-p6-grey-mid cursor-pointer"
                                    >
                                        {isExpanded ? t_showLess : t_showMore}
                                        <PhaseSixIcon
                                            name="chevron-down"
                                            className={`transition-all ${isExpanded ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderShareButton = () => (
        <AreaButton
            data-testid="button-share-vocab-training"
            variant={ButtonVariant.PRIMARY}
            colorVariant={ButtonColorVariant.SECONDARY}
            iconName="share"
            iconPosition={IconPosition.RIGHT}
            role="button"
            onClick={props.onShare}
        >
            {t_share}
        </AreaButton>
    );

    return (
        <ClassActivityCardExpandedContent
            dateLabel={props.dateLabel}
            topLeftContent={props.visibilityBadge}
            topRightContent={renderShareButton()}
            selectedSchoolClassId={props.selectedSchoolClassId}
            availableSchoolClasses={props.availableSchoolClasses}
            onSchoolClassChange={handleSchoolClassChange}
            classActivityCategory={ClassActivityCategory.VOCAB_TRAINING}
        >
            {renderVocabWords()}
        </ClassActivityCardExpandedContent>
    );
};
